/**
 * Library list logic (de-slop CHAT-9 / CHAT-11). Pure module so it can be
 * unit-tested; `useLibraryDocuments` does the fetching.
 */
import type { UserDocumentItem } from '../../services/api';
import type { SharedDocument } from '../../types';

/** The backend caps `limit` at 100 (`user_documents.py`). */
export const LIBRARY_PAGE_SIZE = 100;
/**
 * Search and type filters run on the client (file names are encrypted at
 * rest, so there is no server-side search). When the user searches or filters
 * we load the remaining pages first, up to this many documents.
 */
export const LIBRARY_MAX_DOCUMENTS = 1000;

export type LibraryFilter = 'all' | 'transcripts' | 'documents' | 'materials' | 'shared';

const FILTER_TYPES: Record<Exclude<LibraryFilter, 'all' | 'shared'>, string> = {
  transcripts: 'user_transcript',
  documents: 'user_analysis',
  materials: 'user_material',
};

/** Append a page, skipping ids already loaded (a document can shift pages when new ones arrive). */
export function mergeDocumentPages(existing: UserDocumentItem[], page: UserDocumentItem[]): UserDocumentItem[] {
  if (page.length === 0) return existing;
  const seen = new Set(existing.map((item) => item.id));
  const merged = existing.slice();
  for (const item of page) {
    if (!seen.has(item.id)) {
      seen.add(item.id);
      merged.push(item);
    }
  }
  return merged;
}

export function hasMoreDocuments(loadedCount: number, total: number): boolean {
  return loadedCount < total && loadedCount < LIBRARY_MAX_DOCUMENTS;
}

/** Filtering or searching needs every document loaded to be correct. */
export function needsFullList(filter: LibraryFilter, query: string): boolean {
  return query.trim().length > 0 || (filter !== 'all' && filter !== 'shared');
}

function matchesQuery(name: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  return !q || name.toLowerCase().includes(q);
}

export function filterLibraryItems(
  items: UserDocumentItem[],
  filter: LibraryFilter,
  query: string,
): UserDocumentItem[] {
  if (filter === 'shared') return [];
  return items.filter((item) => (
    (filter === 'all' || item.document_type === FILTER_TYPES[filter])
    && matchesQuery(item.original_filename, query)
  ));
}

export function filterSharedItems(items: SharedDocument[], query: string): SharedDocument[] {
  return items.filter((item) => matchesQuery(item.original_filename, query));
}

/**
 * Per-type pill counts. They are only exact once every document is loaded;
 * before that, return null so the pill shows no number rather than a count
 * that disagrees with "All" (CHAT-11).
 */
export function typeCount(
  items: UserDocumentItem[],
  filter: Exclude<LibraryFilter, 'all' | 'shared'>,
  fullyLoaded: boolean,
): number | null {
  if (!fullyLoaded) return null;
  return items.filter((item) => item.document_type === FILTER_TYPES[filter]).length;
}

export type LibraryStatus = 'material' | 'processing' | 'failed' | 'analyzed' | 'transcribed' | 'notAnalyzed';

/**
 * Row status (CHAT-9). A finished voice transcript used to show "Analyzed"
 * unconditionally. The list API only links a chat session for text-analysis
 * documents, so a transcript is shown as "Transcribed" (the next step is an
 * analysis) unless the row itself says it has one.
 */
export function libraryItemStatus(item: UserDocumentItem | SharedDocument): LibraryStatus {
  if (item.document_type === 'user_material') return 'material';
  if (item.document_type === 'user_transcript') {
    if (item.transcription_status === 'processing' || item.transcription_status === 'pending') return 'processing';
    if (item.transcription_status === 'failed') return 'failed';
    const sessionId = 'session_id' in item ? item.session_id : undefined;
    return sessionId ? 'analyzed' : 'transcribed';
  }
  const analyzed = 'session_id' in item ? Boolean(item.session_id || item.processed) : false;
  return analyzed ? 'analyzed' : 'notAnalyzed';
}
